"use client";

import { useEffect, useRef } from "react";
import { Check, Loader2, Pencil, Plus } from "@/components/icons";

import { cn } from "@/lib/utils";
import type {
  InlineComboboxListProps,
  InlineComboboxProps,
  InlineComboboxOption,
} from "./inline-combobox-types";
import { IconButton } from "@/components/ui/icon-button";

function DefaultOptionContent({
  option,
  optionIcon: OptionIcon,
}: {
  option: InlineComboboxOption;
} & Pick<InlineComboboxListProps, "optionIcon">) {
  return (
    <>
      {OptionIcon ? (
        <OptionIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-sm font-medium">{option.label}</span>
          {option.description ? (
            <span className="shrink-0 text-xs text-muted-foreground">
              {option.description}
            </span>
          ) : null}
        </span>
        {option.sublabel ? (
          <span className="block truncate text-xs text-muted-foreground">
            {option.sublabel}
          </span>
        ) : null}
      </span>
      {option.trailing}
    </>
  );
}

function OptionTrailingControl({
  option,
  isSelected,
  onEditOption,
  editAsText,
}: {
  option: InlineComboboxOption;
  isSelected: boolean;
} & Pick<InlineComboboxListProps, "onEditOption" | "editAsText">) {
  if (onEditOption && !editAsText) {
    return (
      <IconButton
        size="inline"
        aria-label={`Edit ${option.label}`}
        className="ml-auto text-muted-foreground"
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => {
          event.stopPropagation();
          onEditOption(option.value);
        }}
      >
        <Pencil />
      </IconButton>
    );
  }
  if (onEditOption) {
    return (
      <span className="ml-auto flex shrink-0 items-center gap-2">
        <button
          type="button"
          aria-label={`Edit ${option.label}`}
          className="flex items-center gap-1 rounded-sm text-xs font-medium text-link hover:text-link-hover focus-visible:outline-2 focus-visible:outline-ring"
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            onEditOption(option.value);
          }}
        >
          <Pencil aria-hidden className="size-3" />
          Edit
        </button>
        {isSelected ? <Check aria-hidden className="h-4 w-4 text-action-fill" /> : null}
      </span>
    );
  }
  return isSelected ? (
    <Check className="ml-auto h-4 w-4 shrink-0 text-action-fill" />
  ) : null;
}

type OptionRowProps = {
  id: string;
  option: InlineComboboxOption;
  isSelected: boolean;
  isActive: boolean;
} & Pick<
  InlineComboboxListProps,
  "optionIcon" | "renderOption" | "onEditOption" | "editAsText" | "onChoose" | "onHighlight"
>;

function useScrollIntoViewWhen(isActive: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isActive) ref.current?.scrollIntoView({ block: "nearest" });
  }, [isActive]);
  return ref;
}

function OptionRow({
  id,
  option,
  isSelected,
  isActive,
  optionIcon,
  renderOption,
  onEditOption,
  editAsText,
  onChoose,
  onHighlight,
}: OptionRowProps) {
  const ref = useScrollIntoViewWhen(isActive);
  return (
    <div
      ref={ref}
      id={id}
      role="option"
      aria-selected={isSelected}
      aria-disabled={option.disabled}
      tabIndex={-1}
      className={cn(
        "flex w-full items-center gap-2 px-3 py-2 text-left transition-colors pointer-coarse:min-h-11",
        option.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        isActive && !option.disabled ? "bg-muted/50" : "hover:bg-muted/50",
      )}
      onMouseDown={(event) => event.preventDefault()}
      onMouseEnter={() => {
        if (!option.disabled) onHighlight(option.value);
      }}
      onClick={() => {
        if (!option.disabled) onChoose(option);
      }}
    >
      {renderOption ? (
        renderOption(option, isSelected)
      ) : (
        <DefaultOptionContent option={option} optionIcon={optionIcon} />
      )}
      <OptionTrailingControl
        option={option}
        isSelected={isSelected}
        onEditOption={onEditOption}
        editAsText={editAsText}
      />
    </div>
  );
}

function ListNotice({
  isLoading,
  message,
}: {
  isLoading: boolean;
  message: string | undefined;
}) {
  if (isLoading) {
    return (
      <div role="status" className="p-3 text-center">
        <Loader2
          aria-hidden="true"
          className="mx-auto h-4 w-4 animate-spin text-muted-foreground"
        />
        <span className="sr-only">Loading options</span>
      </div>
    );
  }
  return (
    <p
      role="status"
      className="px-3 py-4 text-center text-sm text-muted-foreground"
    >
      {message}
    </p>
  );
}

function OptionGroup({
  group,
  listId,
  value,
  activeValue,
  ...rowProps
}: Pick<
  InlineComboboxListProps,
  | "listId"
  | "value"
  | "activeValue"
  | "optionIcon"
  | "renderOption"
  | "onEditOption"
  | "editAsText"
  | "onChoose"
  | "onHighlight"
> & {
  group: InlineComboboxListProps["groups"][number];
}) {
  return (
    <div>
      {group.heading ? (
        <p className="px-3 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">
          {group.heading}
        </p>
      ) : null}
      {group.options.map((option) => (
        <OptionRow
          key={option.value}
          id={`${listId}-${option.value}`}
          option={option}
          isSelected={value === option.value}
          isActive={activeValue === option.value}
          {...rowProps}
        />
      ))}
    </div>
  );
}

export function InlineComboboxList({
  groups,
  isLoading,
  emptyText,
  minSearchMessage,
  ...groupProps
}: InlineComboboxListProps) {
  const isEmpty = groups.every((group) => group.options.length === 0);
  const notice = minSearchMessage || (isEmpty ? emptyText : undefined);
  if (isLoading || notice) {
    return <ListNotice isLoading={Boolean(isLoading)} message={notice} />;
  }
  return (
    <>
      {groups.map((group, groupIndex) => (
        <OptionGroup
          key={group.heading || groupIndex}
          group={group}
          {...groupProps}
        />
      ))}
    </>
  );
}

export function InlineComboboxFooter({
  action,
  footer,
  query,
  close,
}: Pick<InlineComboboxProps, "action" | "footer"> & { query: string; close: () => void }) {
  return (
    <>
      {action ? (
        <div className="border-t border-border">
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              close();
              action.onSelect(query.trim());
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-action-fill transition-colors hover:bg-muted/50 pointer-coarse:min-h-11"
          >
            <Plus className="h-4 w-4" />
            <span className="text-sm font-medium">{typeof action.label === "string" ? action.label : action.label(query)}</span>
          </button>
        </div>
      ) : null}
      {footer ? <div className="border-t border-border">{footer(close)}</div> : null}
    </>
  );
}
